import React, { useEffect, useRef } from "react";

/**
 * MagneticButton — Rend son enfant "magnétique" : quand le curseur s'approche,
 * l'enfant se translate légèrement vers lui. Retour doux à la position initiale.
 *
 * Inspiration : Framer / Apple / Linear.
 *
 * Props :
 *   - as : élément wrapper (défaut "span")
 *   - strength : amplitude max px (défaut 12)
 *   - radius : distance d'activation en px (défaut 120)
 *   - damping : lissage (défaut 0.14)
 */
export default function MagneticButton({
  children,
  as = "span",
  strength = 12,
  radius = 120,
  damping = 0.14,
  className = "",
  style = {},
  ...rest
}) {
  const ref = useRef(null);
  const target = useRef({ x: 0, y: 0 });
  const current = useRef({ x: 0, y: 0 });
  const rafId = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    if (window.matchMedia?.("(hover: none)").matches) return;

    const el = ref.current;
    if (!el) return;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist < radius) {
        const factor = 1 - dist / radius; // 1 au centre → 0 au bord de l'effet
        target.current.x = (dx / radius) * strength * factor;
        target.current.y = (dy / radius) * strength * factor;
      } else {
        target.current.x = 0;
        target.current.y = 0;
      }
    };

    const loop = () => {
      current.current.x += (target.current.x - current.current.x) * damping;
      current.current.y += (target.current.y - current.current.y) * damping;
      if (el) {
        el.style.transform = `translate3d(${current.current.x.toFixed(2)}px, ${current.current.y.toFixed(2)}px, 0)`;
      }
      rafId.current = requestAnimationFrame(loop);
    };
    loop();

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [strength, radius, damping]);

  const Tag = as;
  return (
    <Tag ref={ref} className={className} style={{ display: "inline-block", willChange: "transform", ...style }} {...rest}>
      {children}
    </Tag>
  );
}
